// ─── Header navigation ────────────────────────────────────────────────────────
// Mobile drawer + desktop megamenu behaviour for sections/header.liquid.
//
// All locale-dependent content (French/English labels, locale-prefixed links,
// which collections are non-empty) is resolved on the server in Liquid and
// handed to this file as JSON in <script id="HeaderNavData">. Nothing here
// derives text or availability from the active language, so EN and FR behave
// identically.
//
// Drawer order: leadingLinks[], shop, menu2, menu3, extraLinks[]. Both link
// lists are [{ label, url }]; Liquid decides which list a link belongs to
// (New Arrivals goes in leadingLinks, so it sits first in the drawer).
//
// Architecture (each unit has one job; collaborators are injected)
// ────────────────────────────────────────────────────────────────
//   MenuLabelParser          — rich text → [{ html, color, handle }]
//   createAvailabilityFilter — predicate: hide links to empty collections
//   MobileNavRenderer        — state → HTML string (pure, no DOM access)
//   MobileNavController      — drawer state machine; renders into a container
//   DesktopMegaMenu          — shows/hides the hover panels
//   init                     — reads the data, wires DOM events to the above
//
// Markup contract
//   [data-nav-action="shop|menu2|menu3|category|back"] (+ data-nav-index)
//   [data-megamenu-trigger="<panel id>"]  mouseover → show that panel
//   [data-megamenu-panel]                 mouseleave → hide all panels
// ──────────────────────────────────────────────────────────────────────────────
(function () {
  'use strict';

  const DATA_ELEMENT_ID = 'HeaderNavData';
  const DRAWER_ELEMENT_ID = 'menu-drawer';

  const ICON_RIGHT = '<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#000000"><path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z"/></svg>';
  const ICON_BACK = '<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#000000"><path d="m313-440 224 224-57 56-320-320 320-320 57 56-224 224h487v80H313Z"/></svg>';

  const ITEM_STYLE = 'font-size:14px;letter-spacing:0.2px;cursor:pointer;display:flex;justify-content:space-between;align-items:center';
  const BACK_STYLE = 'display:flex;flex-direction:row;align-items:center;gap:10px;cursor:pointer';
  const SAFE_COLOR = /^[#a-z0-9(),.%\s-]+$/i;

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ── Label parsing ───────────────────────────────────────────────────────
  // Menu columns are authored as rich text: one <p> per link, with an
  // optional colour after the link ("<a>Sale</a>: red"). Mirrors the parsing
  // the desktop panel does in Liquid.
  const MenuLabelParser = {
    parse(richText) {
      return String(richText || '')
        .split('</p>')
        .map((chunk) => chunk.replace(/<p\b[^>]*>/gi, '').trim())
        .filter(Boolean)
        .map((item) => {
          const match = item.match(/^([\s\S]*?<\/a>)\s*:\s*([^<]+?)\s*$/);
          const html = match ? match[1] : item;
          const rawColor = match ? match[2].toLowerCase() : '';
          return {
            html,
            color: SAFE_COLOR.test(rawColor) ? rawColor : '',
            handle: MenuLabelParser.extractCollectionHandle(html),
          };
        });
    },

    // Works for /collections/x, /fr/collections/x and ?query / #hash suffixes.
    extractCollectionHandle(html) {
      const match = String(html || '').match(/href="[^"]*\/collections\/([^"?#/]+)/);
      return match ? match[1] : null;
    },
  };

  // Links without a collection handle always show. Handles are compared with
  // the server-computed list, which is built from the very same label text, so
  // a translated collection handle can never cause a mismatch.
  function createAvailabilityFilter(availableHandles) {
    if (!Array.isArray(availableHandles)) return () => true;
    const available = new Set(availableHandles);
    return (item) => !item.handle || available.has(item.handle);
  }

  // ── Rendering (pure: data in, HTML out) ─────────────────────────────────
  class MobileNavRenderer {
    constructor(data) {
      this.data = data || {};
      this.isAvailable = createAvailabilityFilter(this.data.availableHandles);
    }

    render(state) {
      switch (state.view) {
        case 'categories': return this.categories();
        case 'links': return this.links(state.columnIndex);
        case 'menu2': return this.imageMenu(this.topLabel('menu2'), this.data.menu2Images);
        case 'menu3': return this.imageMenu(this.topLabel('menu3'), this.data.menu3Images);
        default: return this.initial();
      }
    }

    topLabel(key) {
      return (this.data.topLevel || {})[key] || '';
    }

    back(label) {
      return `<div data-nav-action="back" style="${BACK_STYLE}">${ICON_BACK}<div>${escapeHtml(label)}</div></div>`;
    }

    initial() {
      const padding = escapeHtml(this.data.drawerPadding || '15px');
      const style = `padding:${padding};${ITEM_STYLE}`;
      const topItems = ['shop', 'menu2', 'menu3'].map((key) => `
        <li data-nav-action="${key}" style="${style}">
          <span>${escapeHtml(this.topLabel(key))}</span>${ICON_RIGHT}
        </li>`);
      const linkItems = (links) => (links || []).map((link) => this.linkItem(link, style));
      // leadingLinks is absent in older data: treated as empty.
      const items = [...linkItems(this.data.leadingLinks), ...topItems, ...linkItems(this.data.extraLinks)];
      return `<ul class="menu-drawer__menu list-menu" role="list">${items.join('')}</ul>`;
    }

    linkItem({ label, url }, style) {
      return `
        <li style="${style}">
          <a href="${escapeHtml(url)}" style="text-decoration:none;color:inherit;width:100%;display:flex;justify-content:space-between;align-items:center">
            <span>${escapeHtml(label)}</span>
          </a>
        </li>`;
    }

    categories() {
      const columns = (this.data.columns || []).map(({ title }, index) => `
        <div data-nav-action="category" data-nav-index="${index}" style="display:flex;justify-content:space-between;align-items:center;cursor:pointer;padding:5px 0">
          <span>${escapeHtml(title)}</span>${ICON_RIGHT}
        </div>`).join('');
      const images = (this.data.section1Images || []).map(({ imageUrl, caption, link }) => `
        <a href="${escapeHtml(link)}" style="margin-left:auto;cursor:pointer;position:relative;font-weight:bold;display:block;text-decoration:none">
          <img style="width:100%;object-fit:cover" src="${escapeHtml(imageUrl)}" alt="${escapeHtml(caption)}">
          <div style="position:absolute;bottom:20px;left:20px;color:white">${escapeHtml(caption)}</div>
        </a>`).join('');
      return `${this.back(this.topLabel('shop'))}
        <div style="display:flex;flex-direction:column;gap:10px;padding:10px">${columns}</div>
        <div style="display:flex;flex-direction:row;gap:10px;padding:10px">${images}</div>`;
    }

    links(columnIndex) {
      const column = (this.data.columns || [])[columnIndex];
      const links = column
        ? MenuLabelParser.parse(column.label)
            .filter(this.isAvailable)
            // Link markup is merchant-authored rich text, rendered as-is.
            .map(({ html, color }) => `<div style="${color ? `color:${color}` : 'color:black'}" class="link-and-label">${html}</div>`)
            .join('')
        : '';
      return `${this.back(column ? column.title : '')}
        <div style="display:flex;flex-direction:column;gap:10px;padding:10px">${links}</div>`;
    }

    imageMenu(title, images) {
      const grid = (images || []).map(({ imageUrl, caption, link }) => `
        <a href="${escapeHtml(link)}" style="display:flex;flex-direction:column;width:fit-content;align-items:start;cursor:pointer;margin:5px auto;text-decoration:none">
          <img style="max-height:150px;width:auto;height:auto;object-fit:cover" src="${escapeHtml(imageUrl)}" alt="${escapeHtml(caption)}">
          <div style="position:relative;bottom:50px;left:20px;font-size:16px;color:white;font-weight:bold">${escapeHtml(caption)}</div>
        </a>`).join('');
      return `${this.back(title)}<div style="margin:auto">${grid}</div>`;
    }
  }

  // ── Mobile drawer state machine ─────────────────────────────────────────
  //   initial ─► categories ─► links
  //   initial ─► menu2 | menu3
  // Back from links → categories; back from anything else → initial.
  class MobileNavController {
    constructor({ container, renderer }) {
      this.container = container;
      this.renderer = renderer;
      this.state = { view: 'initial' };
    }

    handle(action, index) {
      switch (action) {
        case 'shop': return this.show({ view: 'categories' });
        case 'menu2': return this.show({ view: 'menu2' });
        case 'menu3': return this.show({ view: 'menu3' });
        case 'category': return this.show({ view: 'links', columnIndex: Number(index) });
        case 'back': return this.back();
        default: return false;
      }
    }

    back() {
      return this.show(this.state.view === 'links' ? { view: 'categories' } : { view: 'initial' });
    }

    show(state) {
      this.state = state;
      this.container.innerHTML = this.renderer.render(state);
      return true;
    }
  }

  // ── Desktop megamenu ────────────────────────────────────────────────────
  class DesktopMegaMenu {
    constructor(doc) {
      this.doc = doc;
    }

    hideAll() {
      this.doc.querySelectorAll('[data-megamenu-panel]').forEach((panel) => {
        panel.style.display = 'none';
      });
    }

    show(panelId) {
      const panel = this.doc.getElementById(panelId);
      if (!panel || !panel.hasAttribute('data-megamenu-panel')) return false;
      this.hideAll();
      panel.style.display = 'flex';
      return true;
    }
  }

  // ── Wiring ──────────────────────────────────────────────────────────────
  function readNavData(doc) {
    const el = doc.getElementById(DATA_ELEMENT_ID);
    if (!el) {
      console.error('[HeaderNav] #' + DATA_ELEMENT_ID + ' not found');
      return null;
    }
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('[HeaderNav] invalid #' + DATA_ELEMENT_ID + ' JSON', err);
      return null;
    }
  }

  // Binds delegated listeners and returns a teardown function. The controller
  // is built lazily on first tap so the JSON is only parsed if it's needed.
  function init(doc) {
    doc = doc || document;
    const megaMenu = new DesktopMegaMenu(doc);
    let controller = null;

    function getController() {
      const container = doc.getElementById(DRAWER_ELEMENT_ID);
      if (!container) return null;
      if (controller && controller.container === container) return controller;
      const data = readNavData(doc);
      if (!data) return null;
      controller = new MobileNavController({ container, renderer: new MobileNavRenderer(data) });
      return controller;
    }

    function onClick(event) {
      const target = event.target.closest && event.target.closest('[data-nav-action]');
      if (!target || !target.closest('#' + DRAWER_ELEMENT_ID)) return;
      const nav = getController();
      if (nav) nav.handle(target.getAttribute('data-nav-action'), target.getAttribute('data-nav-index'));
    }

    function onMouseOver(event) {
      const trigger = event.target.closest && event.target.closest('[data-megamenu-trigger]');
      if (trigger) megaMenu.show(trigger.getAttribute('data-megamenu-trigger'));
    }

    // mouseleave doesn't bubble, so listen in the capture phase.
    function onMouseLeave(event) {
      if (event.target.matches && event.target.matches('[data-megamenu-panel]')) megaMenu.hideAll();
    }

    doc.addEventListener('click', onClick);
    doc.addEventListener('mouseover', onMouseOver);
    doc.addEventListener('mouseleave', onMouseLeave, true);

    return function teardown() {
      doc.removeEventListener('click', onClick);
      doc.removeEventListener('mouseover', onMouseOver);
      doc.removeEventListener('mouseleave', onMouseLeave, true);
    };
  }

  const api = {
    escapeHtml,
    MenuLabelParser,
    createAvailabilityFilter,
    MobileNavRenderer,
    MobileNavController,
    DesktopMegaMenu,
    init,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api; // unit tests: no auto-init, tests call init() themselves
  } else {
    window.YJHeaderNav = api;
    init(document);
  }
})();
